import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { NAVIGATE_ROUTES } from "../../global/constants";
import { ApiService } from "../api-service/api.service";
import { UserDetailsService } from "../user-details.service";
import { DmsService } from "../dms-service/dms.service";
import { UtilsService } from "../utils-service/utils.service";
import { environment } from "../../../../environments/environment";
import { lastValueFrom } from "rxjs";

@Injectable({
  providedIn: "root",
})
export class SupportService {
  apiEndPoint = NAVIGATE_ROUTES;

  constructor(
    private apiService: ApiService,
    private userDetailsService: UserDetailsService,
    private dmsService: DmsService,
    private utilsService: UtilsService,
    private httpClient: HttpClient
  ) {}

  async startChatSession(data: {
    support_category: string;
    issue_summary: string;
  }) {
    return this.apiService.post(this.apiEndPoint.SUPPORT_START_CHAT, data);
  }

  async createCallRequest(data: {
    support_category: string;
    issue_summary: string;
    name?: string;
    mobile_number?: string;
    isd_code?: string;
  }) {
    return this.apiService.post(this.apiEndPoint.SUPPORT_CALL_REQUEST, data);
  }

  async getMySupportThread() {
    return this.apiService.get(this.apiEndPoint.SUPPORT_THREAD);
  }

  async getMySupportThreadState(nextCursor?: string | null) {
    const cursorQuery = nextCursor
      ? `&cursor=${encodeURIComponent(nextCursor)}`
      : "";
    return this.apiService.get(
      `${this.apiEndPoint.SUPPORT_THREAD}?include_history=true${cursorQuery}`
    );
  }

  async getSupportIssueMessages(issueSessionId: string, page = 1, limit = 50) {
    return this.apiService.get(
      `${this.apiEndPoint.SUPPORT_ISSUE_MESSAGES}${issueSessionId}/messages?page=${page}&limit=${limit}`
    );
  }

  async sendSupportDiscussionMessage(data: {
    message: string;
    issueSessionId: string;
    uploadFile?: any[];
  }) {
    return this.apiService.post(
      `${this.apiEndPoint.SUPPORT_ISSUE_MESSAGE_SEND}${data.issueSessionId}/messages`,
      {
        message: data.message,
        uploadFile: data.uploadFile || [],
      }
    );
  }

  async uploadSupportAttachment(file: File, discussionId: string) {
    const formData = new FormData();
    formData.append("type", "discussion");
    formData.append("id", discussionId);
    formData.append("folder_name", "Support Chat");

    let url: any = await this.fileUploadInS3Bucket([file]);
    url = await this.utilsService.setThumbPathAndUpdateFilePath(url);
    formData.append("attachments", JSON.stringify(url[0]));

    const { data }: any = await this.dmsService.uploadFiles(formData);
    return {
      _id: data?._id,
      thumb_path: data?.thumb_path,
      file_path: data?.file_path,
      type: data?.type,
      size: data?.size,
      name: data?.name,
    };
  }

  getSupportAttachmentDownloadUrl(fileUrl: string, fileName?: string) {
    const params = new URLSearchParams();
    params.set("url", fileUrl || "");
    if (fileName) {
      params.set("name", fileName);
    }
    return `${environment.MS_BFF}support/attachments/download?${params.toString()}`;
  }

  async downloadSupportAttachment(fileUrl: string, fileName?: string) {
    const url = this.getSupportAttachmentDownloadUrl(fileUrl, fileName);
    return lastValueFrom(
      this.httpClient.get(url, {
        responseType: "blob",
      })
    );
  }

  private async fileUploadInS3Bucket(fileArr: File[]): Promise<any[]> {
    const finalURL: any[] = [];
    const fileArrURL = fileArr.map((fileData: File) => {
      const parts = fileData.name.split(".");
      const extension = parts[parts.length - 1] || "";
      return {
        folderName: "/uploads",
        fileName: fileData.name,
        contentType: "." + extension,
      };
    });

    const observableRes = await this.userDetailsService.getPresignURL(fileArrURL);
    const res: any = await lastValueFrom(observableRes);

    await Promise.all(
      fileArr.map(async (fileData: File, index: number) => {
        await this.userDetailsService.s3MediaUpload(
          res?.urls[index]?.url,
          fileData
        );

        const parts = fileArr[index]?.name?.split(".") || [];
        const extension = parts[parts.length - 1] || "";
        finalURL.push({
          data_path: res?.urls[index]?.url
            ?.replace(environment.MEDIA_BUCKET_NAME, environment.DOMAIN)
            .split("?")[0],
          size: fileArr[index]?.size,
          name: fileArr[index]?.name,
          type: "." + extension,
        });
      })
    );

    return finalURL;
  }
}
